import { useRef } from 'react'

/**
 * The {@link useRequestQueue} hook runs the requests handed to it one after
 * another, so their responses are applied in the order they were requested.
 *
 * @usage
 * ```
 *  const { enqueue } = useRequestQueue()
 *
 *  enqueue(() => getCounters(params)).then(setCounters)
 * ```
 *
 * @note
 * A request starts once the previous one has settled, so a response still in
 * flight can never land after, and overwrite, the data a later request brings.
 * A failed request does not block the ones queued behind it.
 *
 * @returns {{ enqueue: Function }}
 */
export function useRequestQueue() {
  const queueRef = useRef(Promise.resolve())

  async function enqueue(request) {
    const next = queueRef.current.then(request)

    queueRef.current = next.catch(() => null)

    return next
  }

  return { enqueue }
}
